import { useState } from 'react';
import {
  Title,
  Stack,
  Card,
  Text,
  Button,
  Alert,
  Group,
  Badge,
  List,
  Divider,
} from '@mantine/core';
import {
  IconSeeding,
  IconAlertCircle,
  IconCircleCheck,
  IconDatabase,
  IconTrash,
} from '@tabler/icons-react';
import { adminService } from '../../services/admin';

type SeedStatus = 'idle' | 'confirm' | 'running' | 'success' | 'error';

interface SeedCardProps {
  title: string;
  description: string;
  details: string[];
  badge: string;
  badgeColor: string;
  onRun: () => Promise<void>;
  destructive?: boolean;
}

function SeedCard({ title, description, details, badge, badgeColor, onRun, destructive }: SeedCardProps) {
  const [status, setStatus] = useState<SeedStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleRun = async () => {
    setStatus('running');
    setErrorMessage('');
    try {
      await onRun();
      setStatus('success');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Seed failed';
      setErrorMessage(message);
      setStatus('error');
    }
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder style={destructive ? { borderColor: 'var(--mantine-color-red-4)' } : undefined}>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Group gap="xs">
              <Text fw={700} size="lg">{title}</Text>
              <Badge color={badgeColor} variant="light">{badge}</Badge>
            </Group>
            <Text c="dimmed" size="sm">{description}</Text>
          </Stack>
        </Group>

        <List size="sm" spacing={4}>
          {details.map((d) => (
            <List.Item key={d}>{d}</List.Item>
          ))}
        </List>

        {status === 'success' && (
          <Alert icon={<IconCircleCheck size={16} />} color="green" variant="light">
            Completed successfully. Refresh the dashboard to see updated counts.
          </Alert>
        )}

        {status === 'error' && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {errorMessage || 'Seed failed. Check the API logs for details.'}
          </Alert>
        )}

        {status === 'confirm' && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            <Stack gap="xs">
              <Text size="sm" fw={600}>This will permanently delete all non-admin users, products, reviews, and comments before reseeding. Admin accounts are preserved. This cannot be undone.</Text>
              <Group gap="xs">
                <Button size="xs" color="red" onClick={handleRun}>Yes, clear and reseed</Button>
                <Button size="xs" variant="subtle" onClick={() => setStatus('idle')}>Cancel</Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {status !== 'confirm' && (
          <Button
            leftSection={destructive ? <IconTrash size={16} /> : <IconSeeding size={16} />}
            loading={status === 'running'}
            disabled={status === 'success'}
            onClick={destructive ? () => setStatus('confirm') : handleRun}
            variant={status === 'success' ? 'light' : destructive ? 'outline' : 'filled'}
            color={status === 'success' ? 'green' : destructive ? 'red' : 'blue'}
          >
            {status === 'running' ? 'Running — this may take a few minutes…' : status === 'success' ? 'Done' : destructive ? 'Clear & Reseed' : 'Run Seed'}
          </Button>
        )}
      </Stack>
    </Card>
  );
}

export default function Seed() {
  return (
    <Stack gap="lg">
      <Group gap="xs">
        <IconDatabase size={28} />
        <Title order={2}>Seed Data</Title>
      </Group>

      <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light">
        Standard seeds skip automatically if data already exists. Use the <strong>Clear & Reseed</strong> variants to wipe all data and start fresh.
      </Alert>

      <SeedCard
        title="Small Seed"
        badge="~25 records"
        badgeColor="gray"
        description="Creates the baseline accounts and a small set of products and reviews for basic testing. Skips if data already exists."
        details={[
          '5 users (admin, moderator, 3 test users)',
          '8 Kirkland Signature products with category IDs',
          '9 reviews with 1 image each',
          'Follows and comments',
        ]}
        onRun={adminService.seedSmall}
      />

      <SeedCard
        title="Clear & Reseed (Small)"
        badge="destructive"
        badgeColor="red"
        description="Wipes all non-admin users, products, reviews, and comments, then runs the small seed fresh. Admin accounts, categories, badges, and reviewer levels are preserved."
        details={[
          'Deletes all non-admin users and transactional data first',
          'Then seeds 5 users, 8 products, 9 reviews, comments',
          'Categories/badges/levels preserved',
        ]}
        onRun={adminService.clearAndSeedSmall}
        destructive
      />

      <Divider />

      <SeedCard
        title="Large Seed"
        badge="~8,600+ records"
        badgeColor="blue"
        description="Creates a realistic large dataset. Takes a few minutes as it uploads ~180 images to Supabase storage. Skips if data already exists."
        details={[
          '100 users with profile images',
          '50 products across 5 brands with category IDs',
          '2,000 reviews (20 per user)',
          '6,000 review images (pooled — 30 unique images reused)',
          '~600 comments across reviews',
        ]}
        onRun={adminService.seedLarge}
      />

      <SeedCard
        title="Clear & Reseed (Large)"
        badge="destructive"
        badgeColor="red"
        description="Wipes all non-admin users, products, reviews, and comments, then runs the full large seed. Admin accounts are preserved. Takes several minutes due to image uploads."
        details={[
          'Deletes all non-admin users and transactional data first',
          'Then seeds 100 users, 50 products, 2,000 reviews, ~600 comments',
          'Categories/badges/levels preserved',
        ]}
        onRun={adminService.clearAndSeedLarge}
        destructive
      />
    </Stack>
  );
}
